/**
 * useHangingProtocol.js
 *
 * Hanging protocol matching engine.
 *
 * Usage:
 *   const { protocol, layout, assignments } = useHangingProtocol(series, studyInfo);
 *
 * Returns:
 *   protocol    — the matched protocol object
 *   layout      — { rows, cols }
 *   assignments — array of { series, wl, label } — one per viewport slot
 *                 series may be null if no match found for that slot
 */

import { useMemo } from 'react';
import protocols from './hangingProtocols';
import { normalizeViewportLayout } from './viewportLayoutGeometry';
import { modalityOf, normalizeModality, pickDefaultMprFusionPair } from './components/fusionSeriesPairing';

// ─── Keyword matching helpers ─────────────────────────────────────────────────

const lower = (s) => (s || '').toLowerCase();

const hasKeyword = (text, keywords) =>
  keywords.some(kw => lower(text).includes(lower(kw)));

const seriesSearchText = (s) => lower([
  s?.seriesDescription,
  s?.protocolName,
  s?.bodyPartExamined,
  s?.laterality,
  s?.imageLaterality,
  s?.viewPosition,
].filter(Boolean).join(' '));

const hasAnySeriesKeyword = (s, keywords = []) =>
  !keywords.length || keywords.some(kw => seriesSearchText(s).includes(lower(kw)));

const lateralityMatches = (s, wanted) => {
  const w = lower(wanted || '');
  if (!w) return true;
  const text = seriesSearchText(s);
  const isLeft = w === 'l' || w === 'left';
  const isRight = w === 'r' || w === 'right';
  if (!isLeft && !isRight) return text.includes(w);

  // Match laterality as a token or standard mammo shorthand (LCC/LMLO, RCC/RMLO).
  // Avoid raw substring checks: "l" or "r" occur in many unrelated words.
  if (isLeft) {
    return /\b(left|lt|l)\b/.test(text) || /\b(lcc|lmlo)\b/.test(text) || /\bl[ _-]*(cc|mlo)\b/.test(text);
  }
  return /\b(right|rt|r)\b/.test(text) || /\b(rcc|rmlo)\b/.test(text) || /\br[ _-]*(cc|mlo)\b/.test(text);
};

const seriesMatchesRequirement = (s, req = {}) => {
  if (req.modality) {
    const mods = Array.isArray(req.modality) ? req.modality : [req.modality];
    if (!mods.map(normalizeModality).includes(modalityOf(s))) return false;
  }
  if (req.minInstances && (s.numInstances || 0) < req.minInstances) return false;
  if (req.keywords && !hasAnySeriesKeyword(s, req.keywords)) return false;
  if (req.laterality && !lateralityMatches(s, req.laterality)) return false;
  return true;
};

const hasRequiredSeriesSet = (series, requirements = []) => {
  const used = new Set();
  return requirements.every(req => {
    const match = series.find((s, idx) => {
      const key = s.seriesInstanceUID || idx;
      return !used.has(key) && seriesMatchesRequirement(s, req);
    });
    if (!match) return false;
    used.add(match.seriesInstanceUID || series.indexOf(match));
    return true;
  });
};

// Score a series description against a keyword list.
// Higher = better match.
const keywordScore = (seriesDesc, keywords) => {
  if (!keywords || !keywords.length) return 0;
  let score = 0;
  for (const kw of keywords) {
    if (lower(seriesDesc).includes(lower(kw))) score += 10;
  }
  return score;
};

// ─── Protocol matching ────────────────────────────────────────────────────────

/**
 * Score a protocol against the study/series data.
 * Returns a numeric score; 0 = no match.
 */
const scoreProtocol = (protocol, series, studyInfo) => {
  const { matchingRules } = protocol;

  // Empty matching rules = universal fallback (always matches, low score)
  if (!matchingRules || Object.keys(matchingRules).length === 0) {
    return 1;
  }

  let score = 0;

  // Check modalities
  if (matchingRules.modalities && matchingRules.modalities.length > 0) {
    const studyModalities = series.map(modalityOf);
    const hasMatchingModality = matchingRules.modalities.some(mod =>
      studyModalities.includes(normalizeModality(mod))
    );
    if (!hasMatchingModality) return 0; // hard requirement
    score += 10;
  }

  // Check body part keywords against study description + series descriptions
  if (matchingRules.bodyPartKeywords && matchingRules.bodyPartKeywords.length > 0) {
    const searchText = [
      studyInfo?.studyDescription || '',
      studyInfo?.bodyPartExamined || '',
      ...series.map(s => s.seriesDescription || ''),
    ].join(' ');

    const kwScore = keywordScore(searchText, matchingRules.bodyPartKeywords);
    if (kwScore === 0) return 0; // body part specified but no match
    score += kwScore;
  }

  // Optional hard requirement for protocols that need specific series present
  // (e.g. left + right tomosynthesis) before outranking a generic fallback.
  if (matchingRules.requiredSeries && matchingRules.requiredSeries.length > 0) {
    if (!hasRequiredSeriesSet(series, matchingRules.requiredSeries)) return 0;
    score += matchingRules.requiredSeries.length * 15;
  }

  return score + protocol.priority;
};

/**
 * Select the best protocol for the given study.
 */
const selectProtocol = (series, studyInfo, protocolList = protocols) => {
  let best = null;
  let bestScore = -1;

  for (const protocol of protocolList) {
    const score = scoreProtocol(protocol, series, studyInfo);
    if (score > bestScore) {
      bestScore = score;
      best = protocol;
    }
  }

  return best || protocolList[protocolList.length - 1] || protocols[protocols.length - 1]; // always return default
};

// ─── Series selection for a viewport slot ────────────────────────────────────

/**
 * Pick the best series for a viewport slot given a selector spec.
 * already: array of series already assigned (to avoid duplicate assignment
 *          unless allowDupe is set).
 * sortedByInstances: series sorted by numInstances descending (pre-computed).
 */
const seriesForSlot = (selector, allSeries, already, sortedByInstances) => {
  const {
    modality,
    prefer = [],
    exclude = [],
    minInstances = 0,
    allowDupe = false,
    fallback = 'first',
    requireAny = [],
    requiredKeywords = [],
  } = selector;

  // Filter by modality
  let candidates = allSeries.filter(s => {
    if (modality) {
      const mods = Array.isArray(modality) ? modality : [modality];
      if (!mods.map(normalizeModality).includes(modalityOf(s))) {
        return false;
      }
    }
    return true;
  });

  // Filter out scouts / localizers / MIP projections / non-diagnostic series (hard exclude)
  candidates = candidates.filter(s => {
    const desc = lower(s.seriesDescription || '');
    if (!selector.allowScouts) {
      if (
        desc.includes('scout')            ||
        desc.includes('localiz')          ||
        desc.includes('sbi(')             ||  // Philips Spectral Base Image — not renderable with standard W/L
        desc.startsWith('sbi')            ||
        /\bsbi\b/.test(desc)              ||
        desc.includes('surview')          ||
        desc.includes(' mip')             ||  // Maximum Intensity Projection
        desc.includes('_mip')             ||
        /\bmip\b/.test(desc)              ||
        desc.includes('maximum intensity') ||
        desc.includes('minimum intensity') ||
        desc.includes('minip')            ||
        desc.includes('dose report')      ||
        desc.includes('dose sr')          ||
        desc.includes('radiation dose')   ||
        desc.includes('exam summary')     ||
        desc.includes('tracker')          ||
        desc.includes('radiation dose information') ||
        desc.includes('for reformat')     ||  // thin-slice MPR source data
        desc.includes('thins for')        ||
        desc.includes('thin for')         ||
        /^thins\b/.test(desc)             ||  // series named "thins ..."
        desc.includes('localizer')
      ) {
        return false;
      }
    }
    return true;
  });

  // Apply explicit exclude/required keywords
  if (exclude.length > 0) {
    candidates = candidates.filter(s =>
      !hasKeyword(s.seriesDescription, exclude)
    );
  }

  const required = requireAny.length ? requireAny : requiredKeywords;
  if (required.length > 0) {
    candidates = candidates.filter(s => hasAnySeriesKeyword(s, required));
  }

  // Mammography-style metadata/description matching (laterality/view position)
  const wantedLaterality = lower(selector.laterality || '');
  const wantedView = lower(selector.viewPosition || selector.view || '');
  if (wantedLaterality || wantedView) {
    const metaMatched = candidates.filter(s => {
      const text = seriesSearchText(s);
      const latOk = !wantedLaterality || lateralityMatches(s, wantedLaterality);
      const viewOk = !wantedView || text.includes(wantedView);
      return latOk && viewOk;
    });
    if (metaMatched.length > 0) candidates = metaMatched;
  }

  // Apply minInstances
  let volumetric = candidates;
  if (minInstances > 0) {
    const filtered = candidates.filter(s => (s.numInstances || 0) >= minInstances);
    if (filtered.length > 0) volumetric = filtered;
    // if nothing passes, fall through to full candidate list
  }

  // Remove already-assigned unless allowDupe
  let available = allowDupe ? volumetric : volumetric.filter(s =>
    !already.some(a => a && a.seriesInstanceUID === s.seriesInstanceUID)
  );

  // If every matching series is already assigned, leave the slot empty unless the
  // protocol explicitly opted into duplication. Generic CT/MR 2x2 fallbacks should
  // not make a one-series study look like four independent series.
  if (available.length === 0 && allowDupe) {
    available = volumetric.length > 0 ? volumetric : candidates;
  }

  // Special fallback: first-ct intentionally ignores previous assignments for
  // protocols that need to repeat the primary CT series.
  if (fallback === 'first-ct') {
    return allSeries.find(s => modalityOf(s) === 'CT') || null;
  }

  if (available.length === 0) {
    return null;
  }

  // Score by prefer keywords
  if (prefer.length > 0) {
    const scored = available.map(s => ({
      series: s,
      score: keywordScore(s.seriesDescription, prefer),
    }));
    scored.sort((a, b) => b.score - a.score || (b.series.numInstances || 0) - (a.series.numInstances || 0));
    const topScore = scored[0]?.score || 0;
    if (topScore > 0) {
      return scored[0].series;
    }
    // No keyword match — fall through to fallback logic
  }

  // Fallback strategies
  const sortedAvail = [...available].sort(
    (a, b) => (b.numInstances || 0) - (a.numInstances || 0)
  );

  const fallbackMap = {
    // Ordinal fallbacks are evaluated after already-assigned series are removed,
    // so each sequential slot should take the next best remaining candidate.
    // Otherwise a 4-series generic CT layout can skip the actual second series.
    'first':          sortedAvail[0],
    'second':         sortedAvail[0],
    'third':          sortedAvail[0],
    'fourth':         sortedAvail[0],
    'largest':        sortedAvail[0],
    'second-largest': sortedAvail[0],
    'third-largest':  sortedAvail[0],
    'fourth-largest': sortedAvail[0],
  };

  return fallbackMap[fallback] || available[0] || allSeries[0] || null;
};


const alignPetCtAssignments = (protocol, assignments, series) => {
  if (protocol?.id !== 'pet-ct') return assignments;
  const { baseSeries, petSeries } = pickDefaultMprFusionPair(series || []);
  if (!baseSeries && !petSeries) return assignments;
  return assignments.map((assignment) => {
    if (baseSeries && ['CT AXIAL', 'FUSION'].includes(assignment.label)) {
      return { ...assignment, series: baseSeries };
    }
    if (petSeries && assignment.label === 'PET AXIAL') {
      return { ...assignment, series: petSeries };
    }
    return assignment;
  });
};

const buildAssignments = (protocol, series, sortedByInstances) => {
  const assigned = [];
  const assignments = protocol.viewports.map((vpDef) => {
    const sel = vpDef.seriesSelector;
    const picked = seriesForSlot(sel, series, assigned, sortedByInstances);

    // Track assignments (unless allowDupe)
    if (picked && !sel.allowDupe) {
      assigned.push(picked);
    }

    return {
      series: picked,
      wl:     vpDef.wl || { ww: 0, wc: 0 },
      label:  vpDef.label || '',
    };
  });
  return alignPetCtAssignments(protocol, assignments, series);
};

// ─── Main hook ────────────────────────────────────────────────────────────────

const useHangingProtocol = (series, studyInfo, protocolOverrides = []) => {
  return useMemo(() => {
    if (!series || series.length === 0) {
      return {
        protocol: null,
        layout: { rows: 2, cols: 2 },
        assignments: [],
      };
    }

    // Pre-sort by numInstances descending (volumetric series first)
    const sortedByInstances = [...series].sort(
      (a, b) => (b.numInstances || 0) - (a.numInstances || 0)
    );

    // Select best protocol. Local overrides are first-class protocols and can outrank bundled defaults.
    const protocolList = [...(Array.isArray(protocolOverrides) ? protocolOverrides : []), ...protocols];
    const protocol = selectProtocol(series, studyInfo, protocolList);

    // Build assignments for each viewport slot and normalize PET/CT pair-sensitive defaults.
    const assignments = buildAssignments(protocol, series, sortedByInstances);

    return {
      protocol,
      layout: normalizeViewportLayout(protocol.layout),
      assignments,
    };
  }, [series, studyInfo, protocolOverrides]);
};

export const __test__ = {
  seriesForSlot,
  selectProtocol,
  alignPetCtAssignments,
  buildAssignments,
};

export default useHangingProtocol;
